import { Col, Row } from 'antd';
import { companyType } from '../D2r';
import { TopFilterComponent } from 'shared/components/AppLayout/TopFilterComponent';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IFilter, topFilterUtil, defaultFilter } from './utils';
import { IPageConfiguration } from 'app/AppAdmin/commonTypes';
import { CustomTablePagination } from '../../../../shared/components/CustomTablePagination';
import errorHandler from '../../../../shared/utils/errorHandler';
import { apiBms } from '../../../../shared/api';
import dayjs from 'dayjs';
import d2rCreditNoteColumns from './columns';
import { defaultPageConfig } from '../../../../constants';
import { fetchAllRecords } from '../../../../shared/api/lightning';
import { envs } from '../../../../shared/utils/env';
import { D2REndpointType } from 'shared/api/misc';
import CreditNoteApprovalModal from './CreditNoteApprovalModal';

const { omsBaseUrl } = envs;

export const CreditNoteList = ({
	company,
	isWms = false
}: {
	company: companyType;
	isWms?: boolean;
}) => {
	const [data, setData] = useState<any[]>([]);
	const [currentCount, setCurrentCount] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);
	const [filter, setFilter] = useState<IFilter>(defaultFilter);
	const [pageConfig, setPageConfig] = useState<IPageConfiguration>(defaultPageConfig);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [isVoucherXmlLoading, setIsVoucherXmlLoading] = useState(false);
	const [isVoucherExcelLoading, setIsVoucherExcelLoading] = useState(false);
	const [showApproveCnModal, setShowApproveCnModal] = useState(false);
	const [creditNoteData, setCreditNoteData] = useState<any>(null);
	const [reloadList, setReloadList] = useState(0);

	const columns = d2rCreditNoteColumns({
		setShowApproveCnModal,
		setCreditNoteData,
		isWms
	});

	const setPageConfigDefault = useCallback(() => {
		setPageConfig(defaultPageConfig);
	}, []);

	function onCSVDownload() {
		fetchAllRecords(D2REndpointType.CREDIT_NOTE, omsBaseUrl, pageConfig, filter, 1);
	}

	const topFilterConfig = useMemo(
		() =>
			topFilterUtil({
				filter,
				setFilter,
				setPageConfigDefault,
				selectedRowKeys,
				onCSVDownload,
				onDownloadCreditVoucherXml,
				onDownloadCreditVoucherExcel,
				isVoucherXmlLoading,
				isVoucherExcelLoading
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filter, setFilter, setPageConfigDefault, selectedRowKeys, isVoucherXmlLoading, isVoucherExcelLoading]
	);

	async function onDownloadCreditVoucherXml() {
		try {
			if (selectedRowKeys.length === 0) {
				throw new Error('No order is selected.');
			}
			setIsVoucherXmlLoading(true);
			const { data } = await apiBms.post('/tally-voucher/download/credit-note/:xml', { orderIds: selectedRowKeys });

			if (data.success) {
				const blob = new Blob([data.xml], { type: 'text/xml' });
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.download = `CreditNoteVoucher${dayjs().format()}`;
				link.href = url;
				link.click();
				setIsVoucherXmlLoading(false);
			} else {
				throw new Error("Voucher for some orders can't be created. Download excel to know the reason.");
			}
		} catch (err) {
			setIsVoucherXmlLoading(false);
			errorHandler(err);
		}
	}
	async function onDownloadCreditVoucherExcel() {
		try {
			if (selectedRowKeys.length === 0) {
				throw new Error('No order is selected.');
			}

			setIsVoucherExcelLoading(true);

			const { data } = await apiBms.post(
				'/tally-voucher/download/credit-note/:excel',
				{ orderIds: selectedRowKeys },
				{ responseType: 'blob' }
			);

			const blob = new Blob([data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,'
			});
			let a = document.createElement('a');
			a.href = URL.createObjectURL(blob);
			a.download = `CreditNoteVoucher${dayjs().format()}`;
			a.click();
			setIsVoucherExcelLoading(false);
		} catch (err) {
			setIsVoucherExcelLoading(false);
			errorHandler(err);
		}
	}

	async function getAllCreditNotes(pageConfiguration: any, filters: any) {
		try {
			setLoading(true);
			const response = await fetchAllRecords('credit-note', omsBaseUrl, pageConfiguration, filters);
			if (response?.data.status) {
				setData(response.data.creditNotes);
				setCurrentCount(response.data.totalRecords);
			} else {
				throw new Error(response?.data.message || 'Could not get payments');
			}
		} catch (error: unknown) {
			errorHandler(error);
		} finally {
			setLoading(false);
		}
	}

	console.log(selectedRowKeys);

	useEffect(() => {
		setFilter((prevFilter: IFilter) => ({ ...prevFilter, company }));
	}, [company]);

	useEffect(() => {
		console.log(filter, pageConfig);
		getAllCreditNotes(pageConfig, filter);
	}, [filter, pageConfig, reloadList]);

	return (
		<Row className="h-100 p-0">
			<Col span={24} className="main">
				{showApproveCnModal && (
					<CreditNoteApprovalModal
						showApproveCnModal={showApproveCnModal}
						creditNoteData={creditNoteData}
						setShowApproveCnModal={setShowApproveCnModal}
						isWms={isWms}
						setReloadList={setReloadList}
					/>
				)}

				<TopFilterComponent {...topFilterConfig} />
				<div className="innerDiv">
					<div className="div w-100 h-100">
						<CustomTablePagination
							columns={columns}
							data={data}
							onChangePage={(current: any, pageSize: any) => setPageConfig((v) => ({ ...v, current, pageSize }))}
							current={Number(pageConfig.current)}
							total={currentCount}
							loading={loading}
							shouldRowSelection={true}
							selectedItem={undefined}
							catalog={undefined}
							selectedRowKeys={undefined}
							setSelectedRowKeys={setSelectedRowKeys}
							setSelectedItem={undefined}
							pageSize={Number(pageConfig.pageSize)}
							sortField={pageConfig.sortField}
							sortOrder={pageConfig.sortField}
						/>
					</div>
				</div>
			</Col>
		</Row>
	);
};
